import { ACTIONS, MESSAGES } from '../../constants';
import { isNumber } from '../../util/guards';
import { createBotTyping } from '../messageCreators';
import {
  changeMessage,
  concatItems,
  disableChangeFeature,
  omit,
} from './chatTransforms';
import { IChatState, IUndelivered, TChatActions } from './types';

const initialState: IChatState = {
  isLocked: false,
  isLiveChat: false,
  isMuted: false,
  conversationId: '',
  items: [],
  undelivered: {},
  unreadCount: 0,
  scrollTo: undefined,
};

export const chatReducer = (
  state = initialState,
  action: TChatActions,
): IChatState => {
  switch (action.type) {
    case ACTIONS.CHAT_ADD_ITEM: {
      return {
        ...state,
        items: concatItems(state.items, action.item),
      };
    }

    case ACTIONS.CHAT_CHANGE_MESSAGE: {
      return {
        ...state,
        isLocked: true,
        items: changeMessage(state.items),
      };
    }

    case ACTIONS.CHAT_RESET: {
      return {
        ...state,
        isLocked: false,
        items: [createBotTyping({ rm: true })],
      };
    }

    case ACTIONS.CHAT_TOGGLE_MUTE: {
      return {
        ...state,
        isMuted: action.isMuted,
      };
    }

    case ACTIONS.CHAT_CHANGE_MODE: {
      return {
        ...state,
        isLocked: false,
        isLiveChat: action.isLiveChat,
        /**
         * When we toggle to live chat
         * then we need to disable all change buttons in the user messages
         */
        items: action.isLiveChat
          ? disableChangeFeature(state.items)
          : state.items,
      };
    }

    case ACTIONS.CHAT_TOGGLE_LOCK: {
      return {
        ...state,
        isLocked: action.isLocked,
      };
    }

    case ACTIONS.CHAT_ADD_UNDELIVERED: {
      return {
        ...state,
        items: state.items.filter((item) => item.type !== MESSAGES.BOT_TYPING),
        isLocked: false,
        undelivered: {
          ...state.undelivered,
          ...action.payload,
        } as IUndelivered,
      };
    }

    case ACTIONS.CHAT_REMOVE_UNDELIVERED: {
      return {
        ...state,
        undelivered: omit(state.undelivered, action.id) as IUndelivered,
      };
    }

    case ACTIONS.CHAT_UPDATE_SESSION: {
      return {
        ...state,
        ...action.payload,
      };
    }

    case ACTIONS.INCREASE_UNREAD_COUNT: {
      const { unreadCount } = state;
      return {
        ...state,
        unreadCount: isNumber(unreadCount) ? unreadCount + 1 : 1,
      };
    }

    case ACTIONS.RESET_UNREAD_COUNT: {
      return {
        ...state,
        unreadCount: 0,
      };
    }

    case ACTIONS.CHAT_SCROLL_TO: {
      return {
        ...state,
        scrollTo: {
          position: action.position,
        },
      };
    }

    case ACTIONS.CHANGE_RESTART_CHAT_PARAMETERS: {
      return {
        ...state,
        restartChatParameters: action.payload,
      };
    }

    case ACTIONS.SET_CHATBOT_DATA_LOADING: {
      return {
        ...state,
        chatbotDataLoading: action.payload,
      };
    }

    default: {
      return state;
    }
  }
};
